/**
 * Subscription Store
 *
 * Zustand store for tracking the user's current Foundry stage/tier.
 * Stage 0 = Free (onboarding only), Stage 1-4 = Paid tiers.
 *
 * In production this will fetch from the API.
 * For now it defaults to Stage 0 (free tier) for development.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type FoundryStage = 0 | 1 | 2 | 3 | 4;

export interface SubscriptionState {
  /** Current stage (0 = free, 1-4 = paid tiers) */
  stage: FoundryStage;
  /** Whether subscription data has been loaded */
  loaded: boolean;
  /** Stripe subscription status */
  status: 'none' | 'active' | 'past_due' | 'canceled' | 'trialing';

  /** Check if user has access to a feature requiring a minimum stage */
  hasAccess: (requiredStage: FoundryStage) => boolean;
  /** Check if user is on free tier */
  isFreeTier: () => boolean;
  /** Set stage (used when fetched from API or after upgrade) */
  setStage: (stage: FoundryStage) => void;
  /** Set subscription status */
  setStatus: (status: SubscriptionState['status']) => void;
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      stage: 0,
      loaded: false,
      status: 'none',

      hasAccess: (requiredStage: FoundryStage) => {
        return get().stage >= requiredStage;
      },

      isFreeTier: () => {
        return get().stage === 0;
      },

      setStage: (stage: FoundryStage) => set({ stage, loaded: true }),

      setStatus: (status) => set({ status }),
    }),
    {
      name: 'aerware-subscription',
    }
  )
);

/**
 * Page access requirements.
 * Maps route paths to the minimum stage needed.
 */
export const PAGE_STAGE_REQUIREMENTS: Record<string, {
  stage: FoundryStage;
  featureName: string;
  description: string;
  capabilities: string[];
}> = {
  '/command-center': {
    stage: 0,
    featureName: 'Command Center',
    description: 'Your central dashboard for managing AI operators, tasks, and team performance.',
    capabilities: [
      'Real-time operator status and task tracking',
      'Daily standup meetings with Irys',
      'Team performance metrics',
      'Task queue management',
    ],
  },
  '/workforce': {
    stage: 0,
    featureName: 'Workforce',
    description: 'Manage your AI team — hire operators, assign roles, and configure their capabilities.',
    capabilities: [
      'AI operator creation and management',
      'Team structure and assignments',
      'Operator performance tracking',
      'Custom role configuration',
    ],
  },
  '/templates': {
    stage: 0,
    featureName: 'Employee Templates',
    description: 'Browse pre-built employee templates to quickly deploy AI team members.',
    capabilities: [
      'Pre-configured role templates',
      'Department-organized templates',
      'Quick operator deployment',
      'Best practice configurations',
    ],
  },
  '/exchange': {
    stage: 0,
    featureName: 'Exchange',
    description: 'Communication hub for all human-AI conversations and inter-agent messaging.',
    capabilities: [
      'Real-time chat with Irys and operators',
      'Message audit trail',
      'Multi-channel conversations',
      'WebSocket-powered real-time updates',
    ],
  },
  '/watchtower': {
    stage: 1,
    featureName: 'Watchtower',
    description: 'Monitor system health, handle escalations, and track task completion.',
    capabilities: [
      'System health monitoring',
      'Escalation management',
      'Task approval workflow',
      'Performance analytics',
    ],
  },
  '/tasks': {
    stage: 1,
    featureName: 'Tasks',
    description: 'Create, assign, and track tasks across your AI workforce.',
    capabilities: [
      'Task creation and assignment',
      'Priority and status management',
      'Operator delegation',
      'Completion tracking',
    ],
  },
  '/marketplace': {
    stage: 2,
    featureName: 'Marketplace',
    description: 'Browse and install MCP tools, services, and integrations for your AI workforce.',
    capabilities: [
      'MCP tool connectors',
      'External service integrations',
      'Custom tool deployment',
      'Third-party marketplace access',
    ],
  },
  '/core': {
    stage: 0,
    featureName: 'Company Core',
    description: 'Your business knowledge base — all the context your AI team needs to work effectively.',
    capabilities: [
      'Centralized business documentation',
      'Operator knowledge sharing',
      'Company context management',
      'Automatic document ingestion',
    ],
  },
  '/credit-history': {
    stage: 1,
    featureName: 'Credit History',
    description: 'Track your AI credit usage, spending patterns, and message history across all operators.',
    capabilities: [
      'Detailed credit usage tracking',
      'Per-operator spending breakdown',
      'Message history and analytics',
      'Cost optimization insights',
    ],
  },
};
